import type { ProfileConfig } from "@worker/config-schema";
import { el } from "../lib/dom";
import { resolveAsset } from "../lib/assets";
import { iconFor, labelFor, type SocialPlatform } from "../lib/social-icons";

/**
 * Compact socials row. Each item is either:
 *   - Link mode: an <a> that opens the URL (noopener, nofollow)
 *   - Text mode: a non-linking pill that reveals text on tap/hover
 *
 * Custom-icon overrides (asset/URL) replace the built-in SVG entirely.
 * `glowColor` tints the icon via `currentColor` and its drop-shadow.
 */
export function mountSocials(
  socials: ProfileConfig["socials"],
): HTMLElement | null {
  if (socials.length === 0) return null;

  const row = el("div", {
    class: "socials",
    role: "list",
    "aria-label": "Social links",
  });

  for (const s of socials) {
    const label = s.label ?? labelFor(s.platform as SocialPlatform);
    const styleParts: string[] = [];
    if (s.glowColor) styleParts.push(`--icon-glow:${s.glowColor}`);
    if (s.glowColor) styleParts.push(`color:${s.glowColor}`);

    // Tooltip: link-mode shows label + URL. Text-mode shows label + text.
    // When text is blank we drop the tooltip entirely so the icon renders
    // as a clean ornament with no hover overlay.
    const tooltip =
      s.mode === "link"
        ? `${label} · ${stripProto(s.url)}`
        : s.text
          ? `${label} · ${s.text}`
          : null;

    const common = {
      class: "social",
      role: "listitem",
      "data-platform": s.platform,
      "data-tooltip": tooltip,
      style: styleParts.join(";") || null,
      "aria-label": label,
    } as const;

    let node: HTMLElement;
    if (s.mode === "link") {
      node = el("a", {
        ...common,
        href: s.url,
        target: "_blank",
        rel: "noopener noreferrer nofollow",
      });
    } else {
      node = el("button", {
        ...common,
        type: "button",
      });
    }

    node.append(renderIcon(s));
    row.append(node);
  }

  return row;
}

function renderIcon(s: ProfileConfig["socials"][number]): Node {
  if (s.icon) {
    const img = el("img", {
      class: "social__img",
      src: resolveAsset(s.icon),
      alt: "",
      draggable: false,
    });
    return img;
  }
  return iconFor(s.platform as SocialPlatform);
}

function stripProto(url: string): string {
  return url.replace(/^https?:\/\//, "").replace(/\/$/, "");
}
